import { candidateService } from '../services'
import { icsFromInterview, interviewTitle } from './ics'

// Contributes booked interviews to the surfaces that aggregate other apps' calendar items:
// the landing-page ActivityWidget (upcoming N days, see spa/src/hooks/useActivity.js) and
// the events calendar (an explicit month window, including the past — see
// spa/src/helpers/calendarProviders.js). Declared via calendarEvents/calendarColor on
// appDefinition.js.
//
// Honour `start`/`end` when given and fall back to `daysAhead`: the widget only knows how
// far ahead it wants, the calendar knows exactly which window it is drawing, and a month in
// the past is unreachable with daysAhead alone.
//
// Unlike kayentaStories' provider, which hand-parses a bare "YYYY-MM-DD" (such a string
// parses as UTC midnight and silently shifts the day), an interview slot arrives as
// wall-clock "YYYY-MM-DDTHH:MM:SS" — `new Date` reads that as local, which is what we want.
//
// `participation` and `ics` are the OPTIONAL half of the provider-item contract, and they are
// what let a colleague sign up for an interview from the landing page instead of only from
// this app's Intervjuprocess page — the same gesture, and the same .ics follow-up, that
// marking interest in an event gets there. Two rules they exist to satisfy:
//
//   1. `toggle` normalizes its own response. The API answers `user_has_signed_up`
//      (deliberately the same shape as events' toggleInterest), but that is a recruitment
//      word; the shared widget speaks `userIsAttending`. Translating here is what keeps
//      useActivity.js and ExternalEventItem free of any recruitment vocabulary.
//   2. The tooltips travel with the item. "Anmäl mig" is right for an interview and wrong for
//      an event, and the shared avatar stack must not have to know which it is showing.
//
// A closure rather than a lookup on appDefinition: participation is per *item* (a past
// interview can simply omit it), and spa/src/apps/index.js keys `apps` by folder name while
// item.source is app.name — `apps[item.source]` would silently be undefined.
export const getCalendarEvents = async ({ daysAhead, start, end }) => {
  const res = start && end
    ? await candidateService.getInterviews({ start, end })
    : await candidateService.getInterviews({ daysAhead })
  const interviews = res.interviews || []

  return interviews.map(iv => ({
    key:   `recruitment:${iv.candidate_interview_uuid}`,
    title: interviewTitle(iv),
    // Instants, on purpose. Every consumer of these two does `new Date(item.start)` —
    // useActivity's compareItems/dateOnly and its ongoing/upcoming split, ActivityWidget's
    // monthLabel, calendarProviders' asCalendarEntry — and the round-trip is lossless. The
    // .ics needs the raw wall-clock string instead, which is why it travels separately in
    // `ics` rather than being derived from these.
    start: new Date(iv.interview_start_timestamp).toISOString(),
    end:   new Date(iv.interview_end_timestamp || iv.interview_start_timestamp).toISOString(),
    url:   `/recruitment/${iv.candidate_uuid}`,
    participation: {
      attendees:       iv.attendees || [],
      userIsAttending: !!iv.user_has_signed_up,
      joinTitle:       'Anmäl mig',
      leaveTitle:      'Avanmäl mig',
      toggle: async () => {
        const r = await candidateService.toggleInterviewSignup(iv.candidate_interview_uuid)
        return { attendees: r.attendees || [], userIsAttending: !!r.user_has_signed_up }
      },
    },
    ics: icsFromInterview(iv),
  }))
}
